import React, { useState, useEffect, useRef } from "react";
import { FaEdit, FaInfoCircle, FaTimes } from "react-icons/fa";
import { API_URL, UserTypes } from "../constants";

const FREQUENCY_OPTIONS = [
  { value: "now", label: "Immédiat" },
  { value: "daily", label: "Quotidien" },
  { value: "weekly", label: "Hebdomadaire" },
  { value: "biweekly", label: "Bihebdomadaire" },
  { value: "monthly", label: "Mensuel" },
];

const InfoPopover = () => {
  const [open, setOpen] = useState(false);
  const isMobile = window.innerWidth < 1024;

  const PopoverContent = () => (
    <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-gray-700 font-normal">
      <div className="flex items-center justify-between mb-2">
        <p className="font-bold text-loginBordure text-sm lg:text-base">Statuts de maintenance</p>
        <FaTimes
          className="cursor-pointer text-gray-400 hover:text-gray-600 ml-4 flex-shrink-0"
          onClick={() => setOpen(false)}
        />
      </div>
      <div className="space-y-2 text-xs lg:text-sm">
        <div className="flex items-start gap-2">
          <span className="mt-1 w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span>
          <p><span className="font-semibold">À jour</span> — Moins de 70% de la fréquence écoulée.</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="mt-1 w-2.5 h-2.5 rounded-full bg-orange-400 flex-shrink-0"></span>
          <p><span className="font-semibold">À prévoir</span> — Dès 70% atteint. Un mail est envoyé selon la fréquence choisie.</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="mt-1 w-2.5 h-2.5 rounded-full bg-red-500 flex-shrink-0"></span>
          <p><span className="font-semibold">À faire</span> — Seuil atteint ou dépassé. Un mail est envoyé selon la fréquence choisie.</p>
        </div>
      </div>
      <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
        <p className="font-semibold mb-1">Exemple (seuil = 1 000h)</p>
        <ul className="space-y-0.5 list-disc list-inside">
          <li>À jour → À prévoir à partir de <span className="font-medium">700h</span></li>
          <li>À prévoir → À faire à partir de <span className="font-medium">1 000h</span></li>
        </ul>
      </div>
    </div>
  );

  return (
    <div className="relative inline-flex items-center ml-2">
      <FaInfoCircle
        className="cursor-pointer text-loginBordure hover:text-blue-900 transition-all duration-200 ease-in-out text-sm lg:text-base flex-shrink-0"
        onClick={() => setOpen(!open)}
      />

      {open && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />

          {isMobile ? (
            <div className="fixed inset-x-0 bottom-0 z-20 p-4">
              <PopoverContent />
            </div>
          ) : (
            <div className="absolute z-20 left-0 top-6 w-80">
              <PopoverContent />
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MachineFrequency = ({ machineId, token }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const userType = user.type;

  const [frequency, setFrequency] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [selectedFrequency, setSelectedFrequency] = useState("");
  const [initialFrequency, setInitialFrequency] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMachineData = async () => {
      try {
        const response = await fetch(`${API_URL}/machines/${machineId}`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        const freq = data.frequency || data.data?.frequency || null;
        setFrequency(freq);
        setSelectedFrequency(freq || "");
        setInitialFrequency(freq || "");
      } catch (error) {
        console.error("Erreur lors de la récupération de la fréquence :", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMachineData();
  }, [machineId, token]);

  const getLabel = (value) =>
    FREQUENCY_OPTIONS.find((o) => o.value === value)?.label || value || "Non définie";

  const handleSaveClick = async () => {
    try {
      const response = await fetch(`${API_URL}/machines/${machineId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ frequency: selectedFrequency }),
      });

      if (response.ok) {
        setFrequency(selectedFrequency);
        setInitialFrequency(selectedFrequency);
        setEditMode(false);
      } else {
        console.error("Erreur lors de la mise à jour de la fréquence");
      }
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la fréquence :", error);
    }
  };

  const handleCancelClick = () => {
    setSelectedFrequency(initialFrequency);
    setEditMode(false);
  };

  if (loading) return null;

  return (
    <div>
      <h4 className="pt-4 pb-4 text-xl 3xl:text-3xl">
        {editMode ? (
          <div className="flex flex-wrap items-center gap-2 mt-4">
            <select
              value={selectedFrequency}
              onChange={(e) => setSelectedFrequency(e.target.value)}
              className="border-2 border-gray-300 rounded-md p-2 text-xs lg:text-xl 3xl:text-3xl"
            >
              {FREQUENCY_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={handleSaveClick}
              className="bg-loginBordure hover:bg-blue-900 text-white rounded-md px-4 py-2 text-xs lg:text-xl"
            >
              Modifier
            </button>
            <button
              onClick={handleCancelClick}
              className="bg-red-500 hover:bg-red-700 text-white rounded-md px-4 py-2 text-xs lg:text-xl"
            >
              Annuler
            </button>
          </div>
        ) : (
          <span className="inline-flex items-center flex-wrap gap-y-1 font-light">
            <div className="inline-flex items-center font-bold underline mr-2 text-xs lg:text-xl 3xl:text-3xl">
              Fréquence d'envoi mail :
              <InfoPopover />
            </div>
            <span className="text-xs lg:text-xl 3xl:text-3xl">{getLabel(frequency)}</span>
            {(userType === UserTypes.SUPER || userType === UserTypes.ADMIN) && (
              <FaEdit
                className="cursor-pointer ml-4 hover:text-blue-500 transition-all duration-200 ease-in-out"
                onClick={() => setEditMode(true)}
              />
            )}
          </span>
        )}
      </h4>
    </div> 
  );
};

export default MachineFrequency;